import { ChildProps, graphql, withApollo, WithApolloClient } from '@apollo/client/react/hoc';
import { theme } from '@arnold/common';
import styled from '@emotion/styled/macro';
import compose from 'lodash.flowright';
import { path } from 'ramda';
import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { CenterView, ErrorMessage, FullScreenWindow, SnackBar, ChannelBanner } from '../components';
import { ChannelReactions } from '../components/ChannelReactions';
import { GetMessagesQuery, GetMessagesQueryVariables, QuestionType } from '../generated/hooks';
import { messagesQuery } from '../graphql/queries';
import auth from '../lib/auth';
import { definitionTypes } from '../lib/common';
import { IChannel, IUserChannel } from '../types';
import { Modals } from './ChatWindow';
import { InputConsumer } from './InputContext';
import InputForm, { ProgressBar } from './InputForm';
import Messages from './Messages';
import Specials from './Specials';
import { orderScaleAnswersInMessages } from '../components/ScaleStatements';
import {
  AUTOSCROLL_DEBOUNCE_MS,
  getChatIdealTop,
  lastMessageId,
  messagesListTouched,
  nextUserPaused,
  PROGRAMMATIC_SCROLL_IGNORE_MS,
  wheelAutoscrollEffect,
} from './chatAutoscroll';
import { isAwaitingLanguageSelection } from './isAwaitingLanguageSelection';

const RefreshButton = styled.button`
  outline: none;
  padding: 8px;
  color: white;
  background-color: #3533cc;
  border: 1px solid #3533cc;
  border-radius: 5px;
  font-size: 16px;
  cursor: pointer;
`;

const EmptyMessage = styled.h1`
  text-align: center;
  margin-top: 14px;
  color: #bdbdbd;
`;
const ChatControls = styled.div`
  background: ${theme.colors.backgroundCover.default};
`;

interface IComponentProps extends WithTranslation {
  channel: IChannel;
  values: number[];
  changeLastMessage: (message: any) => void;
  lastMessage: NonNullable<GetMessagesQuery['messages']>[0] | null;
  sendMessage: (message: string, index?: number, values?: number[], id?: string) => void;
  showModal: (
    modalName: Modals,
    show: boolean,
    redirectTo?: string,
  ) => (e: React.MouseEvent<HTMLElement> | null) => void;
  notDeliveredMessages: GetMessagesQuery['messages'];
  toggleLanguageSelector: (value: boolean) => void;
  selectedLanguage: string;
  typing: boolean;
  hideControls: boolean;
  err: any;
  t: any;
  remainingQuestions: number | null;
  hasStartingMessages: boolean;
  displayLinkToOneplace: boolean;
  someUnfilledChannel: boolean;
  someReport: boolean;
  isSending: boolean;
}

interface IState {
  messages: any[];
  hasAnswered: boolean;
  previousRemainingQuestions: number;
}

type Props = ChildProps<WithApolloClient<IComponentProps>, GetMessagesQuery>;

class Chat extends React.Component<Props, IState> {
  static getDerivedStateFromProps(nextProps: Props, prevState: IState) {
    const { data } = nextProps;
    const messages = data && data.messages;
    const hasAnswered = messages ? messages.some((message) => message.createdBy === 'RESPONDENT') : false;
    if (
      messages &&
      messages.length > 0 &&
      messages !== prevState.messages &&
      messages[messages.length - 1].createdBy === 'BOT'
    ) {
      return {
        messages,
        hasAnswered,
      };
    }
    return { hasAnswered };
  }
  scrollDiv: any;
  messageListEndRef = React.createRef<HTMLDivElement>();
  lastPreControlsBlockRef = React.createRef<HTMLDivElement>();
  userPausedAutoscroll = false;
  ignoreScrollStateUntilMs = 0;
  hasSyncedAutoscrollAnchor = false;
  _lastUserScrollTop: number | undefined = undefined;
  private autoscrollDebounceTimer: ReturnType<typeof setTimeout> | undefined;
  private programmaticSyncTimer: ReturnType<typeof setTimeout> | undefined;
  refRemainingQuestions = this.props.remainingQuestions ?? this.props.channel.remainingQuestions;

  state: IState = {
    messages: [],
    hasAnswered: false,
    previousRemainingQuestions: this.props.remainingQuestions ?? this.props.channel.remainingQuestions,
  };

  componentDidMount() {
    const { data, changeLastMessage, channel, showModal } = this.props;
    const messages = data && data.messages;
    if (messages && messages.length > 0) {
      changeLastMessage(messages[messages.length - 1]);
    }
    window.addEventListener('focus', this.handleWindowFocus);

    if (isAwaitingLanguageSelection(channel, messages)) {
      showModal(Modals.CHANGE_LANGUAGE, true)(null);
    }
  }

  componentDidUpdate(prevProps: Props) {
    const { channel, showModal, data, changeLastMessage, toggleLanguageSelector, selectedLanguage } = this.props;
    if (channel.id !== prevProps.channel.id) {
      this.userPausedAutoscroll = false;
      this.hasSyncedAutoscrollAnchor = false;
      this._lastUserScrollTop = undefined;
    }
    const prevData = prevProps.data;
    const prevMessages = path<GetMessagesQuery['messages'] | null>(['messages'], prevData);
    const messages = path<GetMessagesQuery['messages'] | null>(['messages'], data);
    if (prevMessages?.length !== messages?.length) {
      this.userPausedAutoscroll = false;
    }

    const lastMessage = messages && messages.length > 0 ? messages[messages.length - 1] : null;
    const prevLastMessage = prevMessages && prevMessages.length > 0 ? prevMessages[prevMessages.length - 1] : null;
    const rmPrev = prevProps.remainingQuestions ?? prevProps.channel.remainingQuestions;

    const rmActual = this.props.remainingQuestions ?? channel.remainingQuestions;
    const hadContextLast = Boolean(prevProps.lastMessage);
    const listTouched = messagesListTouched(prevMessages, messages, hadContextLast);
    const tailMessageIdChanged = Boolean(
      messages?.length && prevMessages?.length && lastMessageId(prevMessages) !== lastMessageId(messages),
    );
    const remainingChanged = rmPrev !== rmActual;
    const lastMessageFromContextChanged = this.props.lastMessage?.id !== prevProps.lastMessage?.id;
    const isLastGqlMessageScale = Boolean(
      lastMessage && 'definition' in lastMessage && lastMessage.definition.__typename === 'ScaleQuestionDefinition',
    );
    const wasLastGqlMessageScale = Boolean(
      prevLastMessage &&
        'definition' in prevLastMessage &&
        prevLastMessage.definition.__typename === 'ScaleQuestionDefinition',
    );
    const flowChanged = listTouched || tailMessageIdChanged || remainingChanged || lastMessageFromContextChanged;
    if (flowChanged || (isLastGqlMessageScale && !wasLastGqlMessageScale)) {
      if (!this.userPausedAutoscroll) {
        this.userPausedAutoscroll = false;
        this._lastUserScrollTop = undefined;
        this.hasSyncedAutoscrollAnchor = false;
        this.scrollToBottom();
      }
    }

    if (isAwaitingLanguageSelection(channel, messages)) {
      showModal(Modals.CHANGE_LANGUAGE, true)(null);
    }

    if (messages && messages.length > 0 && (listTouched || tailMessageIdChanged)) {
      changeLastMessage(messages[messages.length - 1]);
      toggleLanguageSelector(this.state.hasAnswered);
    }

    if (selectedLanguage !== prevProps.selectedLanguage) {
      this.handleRefresh();
    }
    if (rmPrev !== rmActual) {
      this.refRemainingQuestions = rmActual;
    }
    if (this.props.lastMessage?.id !== prevProps.lastMessage?.id) {
      if (
        [
          definitionTypes[QuestionType.Select],
          definitionTypes[QuestionType.Freetext],
          definitionTypes[QuestionType.PersonMultiselect],
          definitionTypes[QuestionType.SmsContact],
          definitionTypes[QuestionType.EmailContact],
        ].includes(path(['definition', '__typename'], this.props.lastMessage)!)
      ) {
        this.refRemainingQuestions = rmActual;
        setTimeout(() => {
          this.setState({ previousRemainingQuestions: this.refRemainingQuestions });
        }, 1500);
      }
    }
  }

  componentWillUnmount() {
    if (this.autoscrollDebounceTimer !== undefined) {
      clearTimeout(this.autoscrollDebounceTimer);
      this.autoscrollDebounceTimer = undefined;
    }
    if (this.programmaticSyncTimer !== undefined) {
      clearTimeout(this.programmaticSyncTimer);
      this.programmaticSyncTimer = undefined;
    }
    window.removeEventListener('focus', this.handleWindowFocus);
  }

  private handleWindowFocus = () => {
    this.props?.data?.refetch({
      channelId: this.props.channel.id,
    });
  };

  handleChatWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    if (e.ctrlKey) return;
    const el = this.scrollDiv;
    if (!el) return;
    const ideal = getChatIdealTop(el, this.messageListEndRef.current, this.lastPreControlsBlockRef.current);
    const w = wheelAutoscrollEffect(e.deltaY, el.scrollTop, ideal);
    if (w === 'pause') this.userPausedAutoscroll = true;
    if (w === 'resume') this.userPausedAutoscroll = false;
  };

  private syncAutoscrollAnchorAfterProgrammatic() {
    const el = this.scrollDiv;
    if (el) {
      this._lastUserScrollTop = el.scrollTop;
    }
    this.hasSyncedAutoscrollAnchor = true;
  }

  handleChatScroll = () => {
    const el = this.scrollDiv;
    if (!el || Date.now() < this.ignoreScrollStateUntilMs) return;
    const st = el.scrollTop;
    const ideal = getChatIdealTop(el, this.messageListEndRef.current, this.lastPreControlsBlockRef.current);
    this.userPausedAutoscroll = nextUserPaused(
      this.userPausedAutoscroll,
      this.hasSyncedAutoscrollAnchor,
      st,
      ideal,
      this._lastUserScrollTop,
    );
    this._lastUserScrollTop = st;
  };

  scrollToBottom = (options?: { behavior?: ScrollBehavior }) => {
    if (this.props.channel.isFinished || new URL(window.location.href).searchParams.get('reactionId') != null) return;
    const behavior = options?.behavior ?? 'smooth';
    if (this.autoscrollDebounceTimer !== undefined) {
      clearTimeout(this.autoscrollDebounceTimer);
      this.autoscrollDebounceTimer = undefined;
    }
    if (this.programmaticSyncTimer !== undefined) {
      clearTimeout(this.programmaticSyncTimer);
      this.programmaticSyncTimer = undefined;
    }
    this.autoscrollDebounceTimer = setTimeout(() => {
      this.autoscrollDebounceTimer = undefined;
      const el = this.scrollDiv;
      if (!el || this.userPausedAutoscroll) return;
      const target = getChatIdealTop(el, this.messageListEndRef.current, this.lastPreControlsBlockRef.current);
      this.ignoreScrollStateUntilMs = Date.now() + PROGRAMMATIC_SCROLL_IGNORE_MS;
      el.scroll({ top: target, behavior });
      const sync = () => {
        if (this.programmaticSyncTimer !== undefined) {
          clearTimeout(this.programmaticSyncTimer);
          this.programmaticSyncTimer = undefined;
        }
        this.programmaticSyncTimer = setTimeout(() => {
          this.programmaticSyncTimer = undefined;
          this.syncAutoscrollAnchorAfterProgrammatic();
        }, PROGRAMMATIC_SCROLL_IGNORE_MS);
      };
      requestAnimationFrame(() => {
        if (el && !this.userPausedAutoscroll) {
          const t2 = getChatIdealTop(el, this.messageListEndRef.current, this.lastPreControlsBlockRef.current);
          if (Math.abs(t2 - el.scrollTop) > 2) {
            el.scroll({ top: t2, behavior });
          }
        }
        sync();
      });
    }, AUTOSCROLL_DEBOUNCE_MS);
  };

  handleRefresh = () => {
    const { data } = this.props;
    if (data) {
      setTimeout(() => data.refetch(), 100);
    }
  };

  handleRef = (scroll: any) => {
    this.scrollDiv = scroll;
  };

  render() {
    const {
      data,
      channel,
      sendMessage,
      notDeliveredMessages,
      values,
      typing,
      hideControls,
      err,
      isSending,
      t,
      hasStartingMessages,
    } = this.props;
    const originalMessages = data && data.messages;

    if (data && data.error) {
      return (
        <FullScreenWindow>
          <CenterView>
            <ErrorMessage>{t('default')}</ErrorMessage>
            <RefreshButton onClick={this.handleRefresh}>{t('tryAgain')}</RefreshButton>
          </CenterView>
        </FullScreenWindow>
      );
    }
    const totalQuestions = channel.totalQuestions || 1;
    const remainingQuestions = this.state.previousRemainingQuestions;

    const messages = orderScaleAnswersInMessages(originalMessages);
    const awaitingLanguageSelection = isAwaitingLanguageSelection(channel, originalMessages);

    return (
      <>
        <FullScreenWindow
          data-cy="talk-page"
          ref={this.handleRef}
          onScroll={this.handleChatScroll}
          onWheel={this.handleChatWheel}
        >
          {this.props.channel && this.props.channel.surveyFinished && !this.props.channel.public && (
            <ChannelBanner channel={this.props.channel} />
          )}
          {this.props.channel && (this.props.channel.reactions?.length ?? 0) > 0 && (
            <ChannelReactions channel={this.props.channel} messages={data?.messages ?? []} />
          )}
          {err !== null && <SnackBar message="Vyskytl se problém, pracujeme na opravě" />}
          {!awaitingLanguageSelection && messages && (
            <Messages
              messages={messages as any}
              typing={typing}
              notDeliveredMessages={notDeliveredMessages!}
              channel={channel}
              sendMessage={sendMessage}
              disabled={data?.loading}
              displayLinkToOneplace={this.props.displayLinkToOneplace}
              someUnfilledChannel={this.props.someUnfilledChannel}
              someReport={this.props.someReport}
              messageListEndRef={this.messageListEndRef}
              lastPreControlsBlockRef={this.lastPreControlsBlockRef}
            />
          )}
          {!awaitingLanguageSelection &&
            (!messages || messages.length === 0) &&
            data &&
            !data.loading &&
            auth.isLoggedIn() &&
            !hasStartingMessages &&
            (channel.isFinished ? (
              <EmptyMessage className="h1">{t('ClosedChannelAccess')}</EmptyMessage>
            ) : (
              <EmptyMessage className="h1">{t('noMessages')}</EmptyMessage>
            ))}
          <ChatControls data-cy="chat-specials">
            {!awaitingLanguageSelection &&
              [definitionTypes[QuestionType.Select], definitionTypes[QuestionType.PersonMultiselect]].includes(
                path(['definition', '__typename'], this.props.lastMessage)!,
              ) && <ProgressBar width={((totalQuestions - remainingQuestions) / totalQuestions) * 100} />}
            {!awaitingLanguageSelection && !channel.isFinished && !hideControls && (
              <Specials
                data={data}
                values={values}
                scrollToBottom={this.scrollToBottom}
                disabled={err != null || channel == null || channel.isFinished || data?.loading!! || isSending}
              />
            )}
            {!awaitingLanguageSelection && !channel.isFinished && (
              <InputForm
                disabled={err != null || channel == null || channel.isFinished || data?.loading!! || isSending}
                progressWidth={((totalQuestions - remainingQuestions) / totalQuestions) * 100}
              />
            )}
          </ChatControls>
        </FullScreenWindow>
      </>
    );
  }
}

const WithMessagesOperations = compose(
  graphql<{ channel: IUserChannel }, GetMessagesQuery, GetMessagesQueryVariables>(messagesQuery, {
    options: ({ channel }) => ({
      variables: { channelId: channel.id },
      notifyOnNetworkStatusChange: true,
    }),
  }),
);

const ChatBodyWithOperations = withTranslation('error')(withApollo<Props>(WithMessagesOperations(Chat)));

const GetPropsFromProvider = ({
  channel,
  toggleLanguageSelector,
  selectedLanguage,
  showModal,
  hasStartingMessages,
  displayLinkToOneplace,
  someUnfilledChannel,
  someReport,
}: {
  channel: IChannel;
  toggleLanguageSelector: (value: boolean) => void;
  selectedLanguage: string;
  showModal: (
    modalName: Modals,
    show: boolean,
    redirectTo?: string,
  ) => (e: React.MouseEvent<HTMLElement> | null) => void;
  hasStartingMessages: boolean;
  displayLinkToOneplace: boolean;
  someUnfilledChannel: boolean;
  someReport: boolean;
}) => (
  <InputConsumer>
    {(context) => (
      <ChatBodyWithOperations
        channel={channel}
        showModal={showModal}
        hideControls={context.state.hideControls}
        sendMessage={context.sendMessage}
        values={context.state.values}
        typing={context.state.typing}
        displayLinkToOneplace={displayLinkToOneplace}
        someUnfilledChannel={someUnfilledChannel}
        someReport={someReport}
        remainingQuestions={context.state.remainingQuestions}
        err={context.state.err}
        changeLastMessage={context.changeLastMessage}
        lastMessage={context.state.lastMessage}
        notDeliveredMessages={context.state.notDeliveredMessages}
        toggleLanguageSelector={toggleLanguageSelector}
        selectedLanguage={selectedLanguage}
        hasStartingMessages={hasStartingMessages}
        isSending={context.state.isSending}
      />
    )}
  </InputConsumer>
);

export default GetPropsFromProvider;
